import type {
  KambiSportsMultiSelection,
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import type { KambiBetResponsePriceChange } from '@core/Areas/Quickbet/signals'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'

export function updateKambiSelectionWithNewPrice<T extends KambiSportsSelection>(
  selection: T,
  priceChange: NonNullable<Parameters<typeof KambiBetResponsePriceChange>[0]>
): T {
  if (isKambiSportsSingleSelection(selection)) {
    const odds = priceChange[0].odds
    if (!odds) return selection
    return updateSingleSelectionOdds(selection, odds)
  }

  if (isKambiSportsMultiSelection(selection)) {
    if (!priceChange[0].odds) return selection
    return updateMultiSelectionOdds(selection, priceChange)
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    const odds = priceChange[0].odds
    if (!odds) return selection
    return updateSameGameMultiSelectionOdds(selection, odds)
  }

  return selection
}

// =============
// Local Helpers
// =============

function updateSingleSelectionOdds<T extends KambiSportsSingleSelection>(
  selection: T,
  odds: number
): T {
  return {
    ...selection,
    betOffer: {
      ...selection.betOffer,
      outcomes: [{ ...selection.betOffer.outcomes[0], odds }],
    },
  }
}

function updateMultiSelectionOdds<T extends KambiSportsMultiSelection>(
  selection: T,
  priceChange: NonNullable<Parameters<typeof KambiBetResponsePriceChange>[0]>
): T {
  return {
    ...selection,
    legs: selection.legs.map(leg => {
      if (leg.betSelectionType === 'single') {
        const legPriceChange = priceChange.find(
          ({ outcomeIds }) =>
            outcomeIds.length === 1 && outcomeIds.includes(leg.betOffer.outcomes[0].id)
        )
        return legPriceChange?.odds ? updateSingleSelectionOdds(leg, legPriceChange.odds) : leg
      }

      if (leg.betSelectionType === 'same-game-multi') {
        const legPriceChange = priceChange.find(
          ({ outcomeIds }) =>
            outcomeIds.length > 1 &&
            leg.betOffers.every(betOffer => outcomeIds.includes(betOffer.outcomes[0].id))
        )
        return legPriceChange?.odds
          ? updateSameGameMultiSelectionOdds(leg, legPriceChange.odds)
          : leg
      }

      return leg
    }),
  }
}

function updateSameGameMultiSelectionOdds<T extends KambiSportsSameGameMultiSelection>(
  selection: T,
  odds: number
): T {
  return { ...selection, odds }
}
